import { useState, useCallback } from 'react';
import { playbookApi } from '../api/playbookApi';
import type {
  VersionHistoryEntry,
  PlaybookDiff,
  VersionCompatibility,
} from '../types/playbook.types';

export function usePlaybookVersions(slug: string | null) {
  const [versions, setVersions] = useState<VersionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diff, setDiff] = useState<PlaybookDiff | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [compatibility, setCompatibility] = useState<VersionCompatibility | null>(null);

  const loadVersions = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const res = await playbookApi.getVersionHistory(slug);
      const data = res as any;
      setVersions(Array.isArray(data) ? data : data.versions || []);
    } catch (err) {
      console.error('[usePlaybookVersions] Failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadDiff = useCallback(async (sourceId: string, targetId: string) => {
    setDiffLoading(true);
    try {
      const res = await playbookApi.getPlaybookDiff(sourceId, targetId);
      setDiff(res as any);
    } catch (err) {
      console.error('[usePlaybookVersions] Diff failed:', err);
    } finally {
      setDiffLoading(false);
    }
  }, []);

  const loadCompatibility = useCallback(async (sourceId: string, targetId: string) => {
    try {
      const res = await playbookApi.getVersionCompatibility(sourceId, targetId);
      setCompatibility(res as any);
    } catch (err) {
      console.error('[usePlaybookVersions] Compatibility failed:', err);
    }
  }, []);

  const clearDiff = useCallback(() => setDiff(null), []);

  return {
    versions, loading, error, diff, diffLoading, compatibility,
    loadVersions, loadDiff, loadCompatibility, clearDiff,
  };
}
